import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/context/AuthContext";
import { toast } from "sonner";
import { ChevronRight, ChevronLeft, Users, MessageSquareText, Palette, Eye, FileText, Download, Loader2, Sparkles, Trash2, Plus, Pencil, Upload, Search } from "lucide-react";

const STEPS = ["Cliente", "Descripción", "Template", "Vista Previa", "Descargar"];

export default function QuotationBuilderPage() {
  const { qid } = useParams();
  const isEditing = !!qid;
  const [step, setStep] = useState(0);
  const [clients, setClients] = useState([]);
  const [clientSearch, setClientSearch] = useState("");
  const [templates, setTemplates] = useState([]);
  const [platformTemplates, setPlatformTemplates] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [title, setTitle] = useState("Propuesta Comercial");
  const [currency, setCurrency] = useState("USD");
  const [description, setDescription] = useState("");
  const [items, setItems] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [bonuses, setBonuses] = useState([]);
  const [notes, setNotes] = useState("");
  const [validityDays, setValidityDays] = useState(30);
  const [proposalContent, setProposalContent] = useState(null);
  const [extraSections, setExtraSections] = useState([]);
  const [designOverrides, setDesignOverrides] = useState({});
  const [designInstructions, setDesignInstructions] = useState("");
  const [quotationId, setQuotationId] = useState(null);
  const [editVersion, setEditVersion] = useState(1);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/clients").then(r => setClients(r.data)).catch(() => {});
    api.get("/templates").then(r => setTemplates(r.data)).catch(() => {});
    api.get("/templates/platform").then(r => setPlatformTemplates(r.data)).catch(() => {});
    if (qid) loadQuotation(qid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qid]);

  const loadQuotation = async (id) => {
    try {
      const res = await api.get(`/quotations/${id}`);
      const q = res.data;
      if (q.status !== "creation") {
        toast.error("Solo puedes editar cotizaciones en etapa 'En Creación'");
        navigate("/app/quotations"); return;
      }
      setSelectedClient(q.client_id); setTitle(q.title); setSelectedTemplate(q.template_id);
      setItems(q.items || []); setDiscounts(q.discounts || []); setBonuses(q.bonuses || []);
      setNotes(q.notes || ""); setCurrency(q.currency || "USD"); setValidityDays(q.validity_days || 30);
      setProposalContent(q.proposal_content || null); setExtraSections(q.extra_sections || []);
      setDesignOverrides(q.design_overrides || {});
      setDesignInstructions(q.design_instructions || "");
      setDescription(q.ai_description || "");
      setQuotationId(q.id); setEditVersion(q.version || 1);
      setStep(3);
    } catch { toast.error("Error al cargar cotización"); navigate("/app/quotations"); }
  };

  const subtotal = items.reduce((s, i) => s + (parseFloat(i.quantity) || 0) * (parseFloat(i.unit_price) || 0), 0);
  const totalDisc = discounts.reduce((s, d) => s + (d.type === "percentage" ? subtotal * (parseFloat(d.value) || 0) / 100 : (parseFloat(d.value) || 0)), 0);
  const total = subtotal - totalDisc;

  const handleAIFormat = async () => {
    if (!description.trim()) return toast.error("Escribe una descripción");
    setAiLoading(true);
    try {
      const res = await api.post("/quotations/ai-format", { description, template_id: selectedTemplate, currency, client_id: selectedClient });
      setItems(res.data.items || []);
      setDiscounts(res.data.discounts || []);
      setBonuses(res.data.bonuses || []);
      setNotes(res.data.notes || "");
      if (res.data.proposal_content) setProposalContent(res.data.proposal_content);
      if (res.data.extra_sections) setExtraSections(res.data.extra_sections);
      const dov = res.data.design_overrides || {};
      setDesignOverrides(dov);
      const di = res.data.design_instructions || "";
      setDesignInstructions(di);
      if (Object.keys(dov).length > 0 || di) {
        toast.success("La IA formateó tu cotización y aplicará los ajustes de formato solo a este documento");
      } else {
        toast.success("La IA ha formateado tu cotización");
      }
      setStep(3);
    } catch { toast.error("Error al formatear con IA"); }
    finally { setAiLoading(false); }
  };

  const fmtC = (val) => {
    if (!val && val !== 0) return "-";
    const v = typeof val === "string" ? parseFloat(val) || 0 : val;
    const isWhole = v === Math.floor(v);
    if (currency === "CLP") return `$${Math.round(v).toLocaleString("es-CL")}`;
    if (isWhole) {
      const f = Math.floor(v).toLocaleString("es-CL");
      if (currency === "UF") return `${f} UF`;
      if (currency === "EUR") return `€${f}`;
      return `USD $${f}`;
    }
    const f = v.toLocaleString("es-CL", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (currency === "UF") return `${f} UF`;
    if (currency === "EUR") return `€${f}`;
    return `USD $${f}`;
  };

  const handlePreview = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/quotations/preview-pdf`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("qf_token")}` },
        body: JSON.stringify({ client_id: selectedClient, title, template_id: selectedTemplate, items, discounts, bonuses, notes, currency, validity_days: validityDays, proposal_content: proposalContent, extra_sections: extraSections, design_overrides: designOverrides, design_instructions: designInstructions })
      });
      const blob = await res.blob();
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(blob));
    } catch { toast.error("Error generando preview"); }
    finally { setLoading(false); }
  };

  const handleCreate = async () => {
    setLoading(true);
    try {
      const payload = { client_id: selectedClient, title, template_id: selectedTemplate, items, discounts, bonuses, notes, currency, validity_days: validityDays, proposal_content: proposalContent, extra_sections: extraSections, ai_description: description, design_overrides: designOverrides, design_instructions: designInstructions };
      if (isEditing) {
        await api.put(`/quotations/${qid}`, payload);
        const pdf = await api.post(`/quotations/${qid}/generate-pdf?template_id=${selectedTemplate || ""}`);
        setPdfUrl(pdf.data.pdf_url);
        toast.success(`Cotización V${editVersion} actualizada`);
        setStep(4);
      } else {
        const res = await api.post("/quotations", payload);
        setQuotationId(res.data.id);
        const pdf = await api.post(`/quotations/${res.data.id}/generate-pdf?template_id=${selectedTemplate || ""}`);
        setPdfUrl(pdf.data.pdf_url);
        toast.success("Cotización creada y PDF generado!");
        setStep(4);
      }
    } catch { toast.error("Error al guardar cotización"); }
    finally { setLoading(false); }
  };

  const handleDownload = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/quotations/${quotationId}/download`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("qf_token")}` }
      });
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `Cotizacion_${title.replace(/\s/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch { toast.error("Error"); }
  };

  const updateItem = (i, f, v) => setItems(items.map((item, idx) => idx === i ? { ...item, [f]: v } : item));
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));
  const addItem = () => setItems([...items, { service_name: "", description: "", quantity: 1, unit_price: 0 }]);

  const SEC_TYPE_LABELS = { timeline: "Cronograma", phases: "Fases", flow: "Flujo", text: "Texto", table: "Tabla" };
  const updateExtraSection = (si, field, value) => { const u = [...extraSections]; u[si] = { ...u[si], [field]: value }; setExtraSections(u); };
  const removeExtraSection = (si) => setExtraSections(extraSections.filter((_, i) => i !== si));
  const updateSectionContent = (si, ci, field, value) => { const u = [...extraSections]; const c = [...(u[si].content || [])]; c[ci] = { ...c[ci], [field]: value }; u[si] = { ...u[si], content: c }; setExtraSections(u); };
  const updateSectionTextContent = (si, ci, value) => { const u = [...extraSections]; const c = [...(u[si].content || [])]; c[ci] = { text: value }; u[si] = { ...u[si], content: c }; setExtraSections(u); };
  const clearSectionImage = (si) => { const u = [...extraSections]; u[si] = { ...u[si], image_url: "" }; setExtraSections(u); };
  const handleSectionImageUpload = async (si, e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/uploads/image`, {
        method: "POST", headers: { Authorization: `Bearer ${localStorage.getItem("qf_token")}` }, body: formData
      });
      const data = await res.json();
      const u = [...extraSections]; u[si] = { ...u[si], image_url: data.url }; setExtraSections(u);
      toast.success("Imagen adjuntada");
    } catch { toast.error("Error al subir imagen"); }
  };

  return (
    <div data-testid="quotation-builder">
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-900 mb-4 transition-colors"
        data-testid="quotation-back-btn"
      >
        <ChevronLeft size={16} /> Volver
      </button>
      <div className="mb-8">
        <h1 className="text-3xl font-black font-['Satoshi'] tracking-tight text-slate-900">
          {isEditing ? "Editar Cotización" : "Nueva Cotización"}
          {isEditing && <span className="ml-2 text-base font-mono font-bold text-blue-600 bg-blue-50 px-2 py-1 rounded-lg align-middle">V{editVersion}</span>}
        </h1>
        <p className="text-slate-500 mt-1">Paso {step + 1} de {STEPS.length}</p>
      </div>

      {/* Steps */}
      <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
        {STEPS.map((s, i) => (
          <div key={i} className="flex items-center gap-2 flex-shrink-0">
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${i === step ? "bg-blue-600 text-white" : i < step ? "bg-blue-50 text-blue-700" : "bg-slate-100 text-slate-400"}`} data-testid={`step-${i}`}>
              <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-xs font-bold">{i + 1}</span>
              <span className="hidden sm:block">{s}</span>
            </div>
            {i < STEPS.length - 1 && <ChevronRight size={14} className="text-slate-300" />}
          </div>
        ))}
      </div>

      {/* STEP 0: Client + Config */}
      {step === 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm" data-testid="step-client">
          <div className="flex items-center gap-3 mb-6"><Users size={20} className="text-blue-600" /><h2 className="font-bold text-lg font-['Satoshi']">Seleccionar Cliente</h2></div>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Título</label>
              <input type="text" value={title} onChange={e => setTitle(e.target.value)} className="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="quotation-title" />
            </div>
            <div className="flex gap-3">
              <div className="flex-1">
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Moneda</label>
                <select value={currency} onChange={e => setCurrency(e.target.value)} className="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-sm" data-testid="quotation-currency"><option>USD</option><option>CLP</option><option>UF</option><option>EUR</option></select>
              </div>
              <div className="flex-1">
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Validez (días)</label>
                <input type="number" value={validityDays} onChange={e => setValidityDays(parseInt(e.target.value) || 30)} className="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>
          </div>
          {clients.length === 0 ? (
            <div className="text-center py-8"><p className="text-slate-500">No tienes clientes. <button onClick={() => navigate("/app/clients")} className="text-blue-600 font-semibold hover:underline">Crear uno</button></p></div>
          ) : (
            <>
              <div className="relative mb-3">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                <input
                  type="text"
                  value={clientSearch}
                  onChange={e => setClientSearch(e.target.value)}
                  placeholder="Buscar por nombre o RUT..."
                  className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="client-search-input"
                />
              </div>
              {(() => {
                const q = clientSearch.trim().toLowerCase();
                const norm = (v) => (v || "").toString().toLowerCase().replace(/[.\-\s]/g, "");
                const nq = norm(q);
                const filtered = q === "" ? clients : clients.filter(c =>
                  (c.name || "").toLowerCase().includes(q) ||
                  norm(c.rut).includes(nq)
                );
                if (filtered.length === 0) {
                  return <div className="text-center py-6 text-sm text-slate-500">Sin resultados para &quot;{clientSearch}&quot;</div>;
                }
                return (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {filtered.map(c => (
                      <div key={c.id} onClick={() => setSelectedClient(c.id)} className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${selectedClient === c.id ? "border-blue-600 bg-blue-50" : "border-slate-200 hover:border-blue-200"}`} data-testid={`select-client-${c.id}`}>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-slate-900">{c.name}</span>
                          {c.rut && <span className="text-[11px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded font-mono">{c.rut}</span>}
                        </div>
                        <div className="text-sm text-slate-500 mt-0.5">{c.contact_name}{c.contact_email && ` · ${c.contact_email}`}</div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </>
          )}
          <div className="flex justify-end mt-6">
            <button onClick={() => setStep(1)} disabled={!selectedClient} className="rounded-lg bg-slate-900 text-white px-6 py-2.5 text-sm font-medium hover:bg-slate-800 disabled:opacity-50 flex items-center gap-2" data-testid="next-step-1">Siguiente <ChevronRight size={16} /></button>
          </div>
        </div>
      )}

      {/* STEP 1: Natural Language Description */}
      {step === 1 && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm" data-testid="step-description">
          <div className="flex items-center gap-3 mb-4"><MessageSquareText size={20} className="text-blue-600" /><h2 className="font-bold text-lg font-['Satoshi']">Describe tu cotización</h2></div>
          <p className="text-sm text-slate-500 mb-4">Escribe en lenguaje natural qué quieres cobrar, incluyendo precios, garantías, regalías o descuentos. La IA lo formateará profesionalmente.</p>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-4">
            <p className="text-xs text-slate-500 font-medium mb-1">Ejemplo:</p>
            <p className="text-xs text-slate-400 italic">"Necesito cobrar por el desarrollo del proceso que rescata cartolas del banco Santander, USD $3,500 por la licencia completa de 1 año, y le voy a dar como regalía una garantía de 3 meses y la migración de procesos existentes"</p>
          </div>
          <textarea value={description} onChange={e => setDescription(e.target.value)} rows={6} placeholder="Escribe aquí lo que quieres cotizar..." className="w-full px-4 py-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" data-testid="quotation-description" />
          <div className="flex justify-between mt-6">
            <button onClick={() => setStep(0)} className="text-slate-600 hover:bg-slate-100 px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-1"><ChevronLeft size={16} /> Anterior</button>
            <button onClick={() => setStep(2)} disabled={!description.trim()} className="rounded-lg bg-slate-900 text-white px-6 py-2.5 text-sm font-medium hover:bg-slate-800 disabled:opacity-50 flex items-center gap-2" data-testid="next-step-2">Elegir Template <ChevronRight size={16} /></button>
          </div>
        </div>
      )}

      {/* STEP 2: Template Selection */}
      {step === 2 && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm" data-testid="step-template">
          <div className="flex items-center gap-3 mb-6"><Palette size={20} className="text-blue-600" /><h2 className="font-bold text-lg font-['Satoshi']">Elegir Template</h2></div>
          {templates.length > 0 && (
            <>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Mis Templates</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {templates.map(t => (
                  <div key={t.id} onClick={() => setSelectedTemplate(t.id)} className={`p-5 rounded-xl border-2 cursor-pointer transition-all ${selectedTemplate === t.id ? "border-blue-600 bg-blue-50 shadow-md" : "border-slate-200 hover:border-blue-200"}`} data-testid={`tpl-${t.id}`}>
                    <div className="flex gap-1.5 mb-3">
                      {Object.values(t.color_scheme || {}).map((c, i) => <div key={i} className="w-5 h-5 rounded-full border border-white shadow-sm" style={{ backgroundColor: c }} />)}
                    </div>
                    <h3 className="font-bold text-slate-900 text-sm">{t.name}</h3>
                    <p className="text-xs text-slate-500 mt-1">{t.description}</p>
                  </div>
                ))}
              </div>
            </>
          )}
          <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Templates de Plataforma</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {platformTemplates.map(t => (
              <div key={t.id} onClick={() => setSelectedTemplate(t.id)} className={`p-5 rounded-xl border-2 cursor-pointer transition-all ${selectedTemplate === t.id ? "border-blue-600 bg-blue-50 shadow-md" : "border-slate-200 hover:border-blue-200"}`} data-testid={`tpl-${t.id}`}>
                <div className="flex gap-1.5 mb-3">
                  {Object.values(t.color_scheme || {}).map((c, i) => <div key={i} className="w-5 h-5 rounded-full border border-white shadow-sm" style={{ backgroundColor: c }} />)}
                </div>
                <h3 className="font-bold text-slate-900 text-sm">{t.name}</h3>
                <p className="text-xs text-slate-500 mt-1">{t.description}</p>
                <span className="inline-block mt-2 text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full font-semibold">Plataforma</span>
              </div>
            ))}
          </div>
          <div className="flex justify-between">
            <button onClick={() => setStep(1)} className="text-slate-600 hover:bg-slate-100 px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-1"><ChevronLeft size={16} /> Anterior</button>
            <button onClick={handleAIFormat} disabled={aiLoading || !selectedTemplate} className="rounded-lg bg-blue-600 text-white px-6 py-2.5 text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2" data-testid="ai-format-btn">
              {aiLoading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
              {aiLoading ? "Formateando con IA..." : "Formatear con IA y Vista Previa"}
            </button>
          </div>
        </div>
      )}

      {/* STEP 3: Preview & Edit */}
      {step === 3 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="step-preview">
          {/* Left: Editable items */}
          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3"><Eye size={20} className="text-blue-600" /><h2 className="font-bold text-lg font-['Satoshi']">Detalle</h2></div>
              <button onClick={() => setEditing(!editing)} className="text-xs font-medium text-blue-600 flex items-center gap-1 hover:underline" data-testid="toggle-edit"><Pencil size={14} /> {editing ? "Listo" : "Editar"}</button>
            </div>

            {/* Items */}
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Ítems</h3>
            <div className="space-y-2 mb-4">
              {items.map((item, i) => editing ? (
                <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`edit-item-${i}`}>
                  <input value={item.service_name} onChange={e => updateItem(i, "service_name", e.target.value)} className="col-span-4 px-2 py-1.5 rounded border border-slate-200 text-xs" placeholder="Servicio" />
                  <input value={item.description} onChange={e => updateItem(i, "description", e.target.value)} className="col-span-3 px-2 py-1.5 rounded border border-slate-200 text-xs" placeholder="Desc" />
                  <input type="number" value={item.quantity} onChange={e => updateItem(i, "quantity", e.target.value)} className="col-span-1 px-2 py-1.5 rounded border border-slate-200 text-xs text-center" />
                  <input type="number" value={item.unit_price} onChange={e => updateItem(i, "unit_price", e.target.value)} className="col-span-2 px-2 py-1.5 rounded border border-slate-200 text-xs" />
                  <span className="col-span-1 text-xs font-bold text-right">{fmtC((parseFloat(item.quantity)||0)*(parseFloat(item.unit_price)||0))}</span>
                  <button onClick={() => removeItem(i)} className="col-span-1 text-slate-400 hover:text-red-600"><Trash2 size={14} /></button>
                </div>
              ) : (
                <div key={i} className="flex justify-between items-center py-2 border-b border-slate-100" data-testid={`item-${i}`}>
                  <div><div className="font-medium text-sm text-slate-900">{item.service_name}</div><div className="text-xs text-slate-500">{item.description}</div></div>
                  <div className="text-right"><div className="text-sm font-bold">{fmtC((parseFloat(item.quantity)||0)*(parseFloat(item.unit_price)||0))}</div><div className="text-xs text-slate-400">{item.quantity} x {fmtC(parseFloat(item.unit_price||0))}</div></div>
                </div>
              ))}
              {editing && <button onClick={addItem} className="text-xs text-blue-600 font-medium flex items-center gap-1 hover:underline" data-testid="add-item-preview"><Plus size={14} /> Agregar ítem</button>}
            </div>

            {/* Bonuses */}
            {bonuses.length > 0 && (
              <div className="mb-4">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Regalías / Beneficios</h3>
                {bonuses.map((b, i) => (
                  <div key={i} className="flex items-center gap-2 py-1 text-sm text-slate-700">
                    <span className="text-blue-600 font-bold">+</span> {editing ? <input value={b.description} onChange={e => { const nb=[...bonuses]; nb[i]={description: e.target.value}; setBonuses(nb); }} className="flex-1 px-2 py-1 rounded border border-slate-200 text-xs" /> : b.description}
                  </div>
                ))}
              </div>
            )}

            {/* Extra Sections */}
            {extraSections.length > 0 && (
              <div className="mb-4">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Secciones Adicionales</h3>
                {extraSections.map((section, si) => (
                  <div key={si} className="border border-slate-200 rounded-lg p-3 mb-2" data-testid={`extra-section-${si}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        {editing ? <input value={section.title} onChange={e => updateExtraSection(si, "title", e.target.value)} className="text-sm font-medium px-2 py-1 rounded border border-slate-200 flex-1" /> : <span className="font-medium text-sm text-slate-900 truncate">{section.title}</span>}
                        <span className="text-[10px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded-full font-semibold whitespace-nowrap">{SEC_TYPE_LABELS[section.type] || section.type}</span>
                      </div>
                      {editing && <button onClick={() => removeExtraSection(si)} className="text-slate-400 hover:text-red-600 ml-2"><Trash2 size={14} /></button>}
                    </div>
                    <div className="space-y-1 text-xs">
                      {(section.type === "timeline" || section.type === "phases") && section.content?.map((item, ci) => editing ? (
                        <div key={ci} className="grid grid-cols-12 gap-1">
                          <input value={item.name || ""} onChange={e => updateSectionContent(si, ci, "name", e.target.value)} className="col-span-4 px-2 py-1 rounded border border-slate-200" placeholder="Fase" />
                          <input value={item.duration || ""} onChange={e => updateSectionContent(si, ci, "duration", e.target.value)} className="col-span-3 px-2 py-1 rounded border border-slate-200" placeholder="Duración" />
                          <input value={item.description || ""} onChange={e => updateSectionContent(si, ci, "description", e.target.value)} className="col-span-5 px-2 py-1 rounded border border-slate-200" placeholder="Descripción" />
                        </div>
                      ) : (
                        <div key={ci} className="flex items-baseline gap-1 text-slate-700">
                          <span className="font-medium text-blue-600">{item.name}</span>
                          <span className="text-slate-400">·</span>
                          <span className="text-slate-500">{item.duration}</span>
                          <span className="text-slate-300 mx-0.5">—</span>
                          <span>{item.description}</span>
                        </div>
                      ))}
                      {section.type === "flow" && section.content?.map((item, ci) => editing ? (
                        <div key={ci} className="grid grid-cols-12 gap-1">
                          <input value={item.step || ""} onChange={e => updateSectionContent(si, ci, "step", e.target.value)} className="col-span-1 px-1 py-1 rounded border border-slate-200 text-center" />
                          <input value={item.name || ""} onChange={e => updateSectionContent(si, ci, "name", e.target.value)} className="col-span-4 px-2 py-1 rounded border border-slate-200" placeholder="Nombre" />
                          <input value={item.description || ""} onChange={e => updateSectionContent(si, ci, "description", e.target.value)} className="col-span-7 px-2 py-1 rounded border border-slate-200" placeholder="Descripción" />
                        </div>
                      ) : (
                        <div key={ci} className="flex items-center gap-2 text-slate-700">
                          <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-[10px] font-bold flex-shrink-0">{item.step}</span>
                          <span className="font-medium">{item.name}</span>
                          <span className="text-slate-300">—</span>
                          <span>{item.description}</span>
                        </div>
                      ))}
                      {section.type === "text" && section.content?.map((item, ci) => editing ? (
                        <textarea key={ci} value={typeof item === "string" ? item : item?.text || ""} onChange={e => updateSectionTextContent(si, ci, e.target.value)} className="w-full px-2 py-1 rounded border border-slate-200 text-xs" rows={2} />
                      ) : (
                        <p key={ci} className="text-slate-700">{typeof item === "string" ? item : item?.text}</p>
                      ))}
                      {section.type === "table" && section.content?.map((item, ci) => (
                        <div key={ci} className="overflow-x-auto">
                          <table className="w-full text-xs border border-slate-200 rounded">
                            <thead><tr className="bg-slate-50">{item.header?.map((h, hi) => <th key={hi} className="px-2 py-1 text-left font-medium text-slate-600 border-b">{h}</th>)}</tr></thead>
                            <tbody>{item.rows?.map((row, ri) => <tr key={ri} className="border-b border-slate-100">{row.map((cell, celli) => <td key={celli} className="px-2 py-1 text-slate-700">{cell}</td>)}</tr>)}</tbody>
                          </table>
                        </div>
                      ))}
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      {section.image_url ? (
                        <div className="flex items-center gap-2">
                          <img src={`${process.env.REACT_APP_BACKEND_URL}${section.image_url}`} alt="" className="h-12 rounded border border-slate-200 object-cover" />
                          {editing && <button onClick={() => clearSectionImage(si)} className="text-xs text-red-500 hover:underline">Eliminar</button>}
                        </div>
                      ) : (
                        <label className="cursor-pointer text-xs text-blue-600 hover:underline flex items-center gap-1" data-testid={`upload-section-image-${si}`}>
                          <Upload size={12} /> Adjuntar imagen
                          <input type="file" className="hidden" accept="image/*" onChange={e => handleSectionImageUpload(si, e)} />
                        </label>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Summary */}
            <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
              <div className="flex justify-between text-sm mb-1"><span className="text-slate-500">Subtotal</span><span>{fmtC(subtotal)}</span></div>
              {totalDisc > 0 && <div className="flex justify-between text-sm mb-1"><span className="text-slate-500">Descuentos</span><span className="text-red-500">- {fmtC(totalDisc)}</span></div>}
              <div className="flex justify-between font-bold text-lg mt-2 pt-2 border-t border-slate-200"><span>Total</span><span>{fmtC(total)}</span></div>
            </div>

            <div className="flex gap-2 mt-4">
              <button onClick={() => setStep(2)} className="flex-1 text-slate-600 hover:bg-slate-100 py-2.5 rounded-lg text-sm font-medium flex items-center justify-center gap-1"><ChevronLeft size={16} /> Cambiar Template</button>
              <button onClick={() => setStep(1)} className="flex-1 text-slate-600 hover:bg-slate-100 py-2.5 rounded-lg text-sm font-medium flex items-center justify-center gap-1"><MessageSquareText size={16} /> Cambiar Descripción</button>
            </div>
          </div>

          {/* Right: PDF Preview */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm flex flex-col">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-bold font-['Satoshi']">Vista Previa PDF</h3>
              <button onClick={handlePreview} disabled={loading} className="text-xs bg-blue-50 text-blue-600 px-3 py-1.5 rounded-lg font-semibold hover:bg-blue-100 flex items-center gap-1 disabled:opacity-50" data-testid="refresh-preview">
                {loading ? <Loader2 size={12} className="animate-spin" /> : <Eye size={12} />} {loading ? "Generando..." : "Actualizar Preview"}
              </button>
            </div>
            <div className="flex-1 p-4 min-h-[500px] bg-slate-50">
              {previewUrl ? (
                <iframe src={previewUrl} className="w-full h-full min-h-[500px] rounded-lg border border-slate-200" data-testid="pdf-preview-iframe" />
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-slate-400">
                  <Eye size={32} className="mb-2" />
                  <p className="text-sm font-medium">Haz click en "Actualizar Preview"</p>
                  <p className="text-xs">para ver cómo quedará tu cotización</p>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-slate-200">
              <button onClick={handleCreate} disabled={loading || items.length === 0} className="w-full rounded-lg bg-slate-900 text-white py-3 text-sm font-medium hover:bg-slate-800 disabled:opacity-50 flex items-center justify-center gap-2" data-testid="confirm-create-btn">
                {loading ? <Loader2 size={16} className="animate-spin" /> : <FileText size={16} />}
                {loading ? "Creando..." : "Confirmar y Crear Cotización"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* STEP 4: Download */}
      {step === 4 && (
        <div className="bg-white rounded-xl border border-slate-200 p-8 shadow-sm text-center" data-testid="step-download">
          <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-4"><FileText size={28} className="text-emerald-600" /></div>
          <h2 className="text-2xl font-bold font-['Satoshi'] text-slate-900 mb-2">¡Cotización lista!</h2>
          <p className="text-slate-500 mb-6">Tu cotización ha sido generada exitosamente</p>
          <div className="bg-slate-50 rounded-lg p-4 mb-6 inline-block border border-slate-200"><div className="text-sm text-slate-500">Total</div><div className="text-3xl font-bold text-slate-900">{fmtC(total)}</div></div>
          <div className="flex justify-center gap-3">
            <button onClick={handleDownload} className="rounded-lg bg-blue-600 text-white px-6 py-3 text-sm font-medium hover:bg-blue-700 flex items-center gap-2 shadow-sm" data-testid="final-download-btn"><Download size={18} /> Descargar PDF</button>
            <button onClick={() => navigate("/app/quotations")} className="rounded-lg bg-white text-slate-900 border border-slate-200 hover:bg-slate-50 px-6 py-3 text-sm font-medium" data-testid="go-quotations">Ver cotizaciones</button>
          </div>
        </div>
      )}
    </div>
  );
}
